import { GridFilterItem, GridFilterModel } from "@mui/x-data-grid-pro";
import { format } from "date-fns";
import { groupBy, isEmpty, isArray, flatMap } from "lodash";

type FilterItem = {
  field: string;
  value: string | string[];
};

/**
 * Configuration for date range field mapping.
 * Field is one column in MUI DataGrid, but API filters uses two values - start and end (date range).
 */
const DATE_RANGE_FIELD_MAPPING: Record<string, { start: string; end: string }> =
  {
    range: { start: "start", end: "end" },
    creationTime: { start: "creationDateStart", end: "creationDateEnd" },
    acceptationTime: {
      start: "acceptationDateStart",
      end: "acceptationDateEnd",
    },
    rejectionTime: { start: "rejectionDateStart", end: "rejectionDateEnd" },
  };

/**
 * Processes a date range filter and returns the appropriate filter items.
 */
const processDateRangeFilter = (
  field: string,
  filter: GridFilterItem
): FilterItem[] => {
  const mapping = DATE_RANGE_FIELD_MAPPING[field];
  if (!mapping) return [];

  const result: FilterItem[] = [];

  if (filter.operator === "onOrAfter") {
    result.push({
      field: mapping.start,
      value: filter.value ? format(filter.value as Date, "yyyy-MM-dd") : "",
    });
  } else if (filter.operator === "onOrBefore") {
    result.push({
      field: mapping.end,
      value: filter.value ? format(filter.value as Date, "yyyy-MM-dd") : "",
    });
  }

  return result;
};

/**
 * Parses and transforms filter models from MUI DataGrid into a standardized format
 * for API requests.
 *
 * This function handles special cases for different field types:
 * - Combines multiple status filters into an array of values
 * - Formats date fields into ISO strings
 * - Transforms range filters into start/end date pairs
 * - Processes creation time filters into specific date range parameters
 *
 * @param filterModel - The filter model from MUI X DataGrid containing filter items
 * @param customFilters - Optional additional filters to include in the result
 * @returns An array of standardized filter objects with field and value properties,
 *          with empty values filtered out
 */
export default (
  filterModel: GridFilterModel,
  customFilters?: Array<{ key: string; value: string[] }>
): FilterItem[] => {
  // Group filters by field using lodash
  const groupedFilters = groupBy(filterModel?.items || [], "field");

  // Process each group of filters
  const processedFilters = flatMap(
    Object.entries(groupedFilters),
    ([field, filters]) => {
      // Special handling for status field
      if (field === "status") {
        // Combine all status values into a single filter with array value
        const statusValues = filters.map((filter) => filter.value as string);
        return [
          {
            field: "statuses",
            value: statusValues,
          },
        ];
      }

      // Special handling for time amount fields
      if (field === "effectiveTimeAmount" || field === "totalTimeAmount") {
        return filters.map((filter) => ({
          field: "absenceRequestType",
          value: filter.value as string,
        }));
      }

      // Handle other fields
      return filters.reduce((result, filter) => {
        if (["start", "end"].includes(field)) {
          result.push({
            field,
            value: filter.value
              ? format(filter.value as Date, "yyyy-MM-dd")
              : "",
          });
        } else if (field in DATE_RANGE_FIELD_MAPPING) {
          // Process date range fields using the common function
          result.push(...processDateRangeFilter(field, filter));
        } else {
          result.push({
            field,
            value: filter.value as string,
          });
        }
        return result;
      }, [] as FilterItem[]);
    }
  );

  // Add custom filters to processed filters
  const customProcessedFilters =
    customFilters && !isEmpty(customFilters)
      ? customFilters.map(({ key, value }) => ({
          field: key,
          value,
        }))
      : [];

  // Combine and filter out empty values
  return [...processedFilters, ...customProcessedFilters].filter(
    ({ value }) =>
      value !== "" &&
      (typeof value === "string"
        ? value.length > 0
        : isArray(value) && value.length > 0)
  );
};
